'use client';
import React, { useState, useEffect, useRef, createContext, useContext } from 'react';
import Image from 'next/image';

// ─── Language Context ─────────────────────────────────────────────────────────
type Lang = 'sq' | 'en';
const LangContext = createContext<{ lang: Lang; setLang: (l: Lang) => void }>({ lang: 'sq', setLang: () => {} });
const useLang = () => useContext(LangContext);

// ─── Modal Context ────────────────────────────────────────────────────────────
const ModalContext = createContext<{ openModal: () => void }>({ openModal: () => {} });
const useModal = () => useContext(ModalContext);

// ─── Scroll Reveal Hook ───────────────────────────────────────────────────────
function useScrollReveal(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); observer.disconnect(); } },
      { threshold }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold]);
  return { ref, visible };
}

// ─── Translations ─────────────────────────────────────────────────────────────
const t = {
  sq: {
    nav: {
      howItWorks: 'Si funksionon',
      faq: 'FAQ',
      contact: 'Kontakt',
      login: 'Hyr',
      register: 'Regjistro biznesin',
    },
    hero: {
      headline1: 'Klientët rezervojnë.',
      headline2: 'Ti fokusohesh te biznesi.',
      subtitle: 'Automatizo rezervimet dhe menaxho gjithçka nga një platformë e vetme.',
      cta1: 'Regjistro biznesin',
      cta2: 'Shiko demonstrimin',
      dashboardTitle: 'Dashboard — Studio Elegance',
      dashboardDate: 'Sot, 4 Gusht 2026',
      live: 'Live',
      stats: [
        { label: 'Rezervime sot', value: '12', color: '#7C3AED' },
        { label: 'Klientë të rinj', value: '5', color: '#10B981' },
        { label: 'Të ardhura', value: '€480', color: '#F97316' },
      ],
      bookings: [
        { name: 'Arta K.', service: 'Prerje flokësh', time: '10:00', status: 'Konfirmuar' },
        { name: 'Besnik H.', service: 'Ngjyrosje', time: '11:30', status: 'Në pritje' },
        { name: 'Mirela S.', service: 'Manikyr', time: '13:00', status: 'Konfirmuar' },
      ],
    },
    social: {
      label: 'Biznese që tashmë po kursejnë kohë',
      stats: [
        { value: '500+', label: 'Biznese aktive' },
        { value: '12,000+', label: 'Rezervime në muaj' },
        { value: '4.9★', label: 'Vlerësim mesatar' },
        { value: '30 min', label: 'Kohë konfigurimi' },
      ],
    },
    why: {
      badge: 'Pse Rezervo Online',
      title: 'Gjithçka që biznesi yt ka nevojë',
      subtitle: 'Nga rezervimet automatike tek menaxhimi i klientëve — e gjitha në një platformë.',
      cards: [
        { icon: '⚡', title: 'Rezervime 24/7', desc: 'Klientët rezervojnë çdo orë të ditës, edhe kur ti fle. Zero thirrje telefonike, zero mesazhe.', glow: 'rgba(93,10,244,0.2)', border: 'rgba(93,10,244,0.3)' },
        { icon: '📅', title: 'Kalendar automatik', desc: 'Çdo rezervim shfaqet menjëherë në kalendarin tënd. Pa konflikte, pa humbje kohe.', glow: 'rgba(16,185,129,0.15)', border: 'rgba(16,185,129,0.25)' },
        { icon: '🔔', title: 'Lista e Pritjes', desc: 'Kur një slot lirohet, sistemi njofton automatikisht klientin tjetër në listë.', glow: 'rgba(249,115,22,0.15)', border: 'rgba(249,115,22,0.25)' },
        { icon: '🔗', title: 'Link personal', desc: 'Çdo biznes merr linkun e vet unik. Ndaje në Instagram, WhatsApp ose bio-n tënd.', glow: 'rgba(124,58,237,0.15)', border: 'rgba(124,58,237,0.25)' },
      ],
    },
    how: {
      badge: 'Si funksionon',
      title: 'Gati në 4 hapa të thjeshtë',
      steps: [
        { num: '1', title: 'Regjistro biznesin', desc: 'Krijo llogarinë tënde falas në 2 minuta.', icon: '🏢' },
        { num: '2', title: 'Personalizo shërbimet', desc: 'Shto shërbimet, çmimet dhe oraret e tua.', icon: '⚙️' },
        { num: '3', title: 'Ndaje linkun', desc: 'Ndaj linkun personal me klientët kudo.', icon: '🔗' },
        { num: '4', title: 'Merr rezervime', desc: 'Klientët rezervojnë vetë — pa asnjë ndërhyrje manuale.', icon: '📲' },
      ],
    },
    dashboard: {
      badge: 'Dashboard',
      title1: 'Menaxho rezervimet',
      title2: 'nga një dashboard i vetëm.',
      subtitle: 'Shiko të gjitha rezervimet, menaxho klientët dhe gjurmo të ardhurat — gjithçka nga një vend i vetëm, në kohë reale.',
      features: ['Rezervime në kohë reale', 'Njoftimet automatike', 'Statistika dhe raporte', 'Menaxhim i stafit'],
      metrics: [
        { label: 'Rezervime këtë javë', value: '47', delta: '+12%', color: '#7C3AED' },
        { label: 'Klientë aktivë', value: '128', delta: '+8%', color: '#10B981' },
        { label: 'Të ardhura (muaj)', value: '€2,340', delta: '+23%', color: '#F97316' },
        { label: 'Shkalla plotësimit', value: '94%', delta: '+5%', color: '#3B82F6' },
      ],
      calendarLabel: 'Kalendari — Gusht 2026',
      upcomingLabel: 'Rezervimet e ardhshme',
      upcoming: [
        { name: 'Arta K.', time: '14:00', service: 'Prerje', color: '#7C3AED' },
        { name: 'Gent M.', time: '15:30', service: 'Ngjyrosje', color: '#10B981' },
      ],
    },
    link: {
      badge: 'Linku personal',
      title1: 'Linku yt personal.',
      title2: 'Rezervimet tua.',
      subtitle: 'Çdo biznes merr linkun e vet unik: rezervo-online.al/emri-yt. Ndaje në Instagram bio, WhatsApp ose kudo tjetër — klientët rezervojnë me 3 klikime.',
      items: ['Ndaj linkun në Instagram bio', 'Klienti rezervon brenda 30 sekondave', 'Ti merr njoftim menjëherë'],
      services: [
        { name: 'Prerje flokësh', price: '€15', time: '45 min' },
        { name: 'Ngjyrosje', price: '€35', time: '90 min' },
        { name: 'Manikyr', price: '€12', time: '30 min' },
      ],
      bookNow: 'Rezervo tani →',
      confirmed: 'Rezervimi u konfirmua!',
      chooseService: 'Zgjedh shërbimin',
      location: 'Tiranë, Shqipëri',
    },
    pricing: {
      badge: 'Çmimi',
      title: 'Gjithçka që të duhet.',
      subtitle: 'Pa surpriza. Pa tarifa të fshehura.',
      planLabel: 'Plan standard',
      trialBadge: '30 DITË FALAS',
      perMonth: '/muaj',
      features: [
        'Rezervime të pakufizuara',
        'Lista e Pritjes',
        'Dashboard i plotë',
        'Link personal unik',
        'Njoftimet automatike',
        'MBËSHTETJE 24/7',
      ],
      cta: 'Regjistro biznesin →',
      note: 'Pa kartë krediti • Anulo kurdo',
    },
    testimonials: {
      badge: 'Çfarë thonë bizneset',
      title: 'Biznese reale. Rezultate reale.',
      items: [
        { name: 'Arta Kelmendi', role: 'Studio Elegance, Tiranë', text: 'Që kur fillova të përdor Rezervo Online, nuk marr më thirrje për rezervime. Klientët rezervojnë vetë dhe unë fokusohem te puna.', avatar: 'A' },
        { name: 'Besnik Hoxha', role: 'Barbershop Hoxha, Durrës', text: 'Kalendari automatik më ka kursyer mbi 2 orë në ditë. Tani mund të pranoj 30% më shumë klientë pa stres shtesë.', avatar: 'B' },
        { name: 'Mirela Shehu', role: 'Beauty Center Mirela, Vlorë', text: 'Lista e pritjes është fantastike. Kur dikush anulon, vendi plotësohet automatikisht. Zero humbje të ardhurash.', avatar: 'M' },
      ],
    },
    faq: {
      badge: 'FAQ',
      title: 'Pyetjet e shpeshta',
      subtitle: 'Gjithçka që duhet të dish para se të fillosh.',
      items: [
        { question: 'Si funksionon Rezervo Online?', answer: 'Regjistro biznesin tënd, personalizo shërbimet dhe oraret, dhe ndaj linkun personal me klientët. Ata rezervojnë vetë — ti merr njoftim menjëherë dhe rezervimi shfaqet automatikisht në dashboard-in tënd.' },
        { question: 'A duhet të kem njohuri teknike?', answer: 'Jo fare. Platforma është projektuar për t\'u përdorur nga çdokush. Konfigurimi zgjat rreth 10 minuta dhe nuk kërkon asnjë njohuri programimi.' },
        { question: 'Çfarë ndodh nëse klienti anullon rezervimin?', answer: 'Klienti mund të anulojë ose ndryshojë rezervimin sipas politikës që ti vendos. Ti merr njoftim menjëherë dhe vendi lirohet automatikisht në kalendar.' },
        { question: 'A mund të kem shumë shërbime dhe staf?', answer: 'Po. Mund të shtosh shërbime të shumta, të caktosh staf të ndryshëm për çdo shërbim dhe të menaxhosh oraret e tyre nga një vend i vetëm.' },
        { question: 'Si funksionon lista e pritjes?', answer: 'Kur një slot është i zënë, klientët mund të regjistrohen në listën e pritjes. Gjithashtu në momentin që një klient rezervon, platforma e pyet "A deshiron te njoftohesh nese krijohet nje orar i lire me pare?" Nese klikon "PO", kur dikush anulon, sistemi njofton automatikisht personat në listë.' },
        { question: 'A mund ta provoj falas?', answer: 'Po, ofrojmë 30 ditë provë falas pa kartë krediti. Pas periudhës së provës, mund të vazhdosh me planin 27€/muaj.' },
      ],
    },
    cta: {
      title1: 'Gati për të automatizuar',
      title2: 'rezervimet?',
      subtitle: 'Bashkohu me qindra biznese shqiptare që tashmë po kursejnë kohë dhe po rrisin rezervimet.',
      cta: 'Regjistro biznesin',
      note: '30 ditë falas • Pa kartë krediti • Anulo kurdo',
    },
    footer: {
      tagline: 'Platforma e rezervimeve për bizneset shqiptare.',
      links: [
        { label: 'Instagram', href: 'https://instagram.com' },
        { label: 'Facebook', href: 'https://facebook.com' },
        { label: 'Kontakt', href: '#footer' },
        { label: 'Kushtet', href: '/terms' },
        { label: 'Privatësia', href: '/privacy' },
      ],
      copy: '© 2026 Rezervo Online. Të gjitha të drejtat e rezervuara.',
    },
    modal: {
      title: 'Regjistro biznesin',
      subtitle: '30 ditë falas • Pa kartë krediti • Anulo kurdo',
      namePlaceholder: 'Emri i biznesit',
      emailPlaceholder: 'Email-i i biznesit',
      phonePlaceholder: 'Numri i telefonit',
      submit: 'Fillo provën falas →',
      submitting: 'Duke dërguar...',
      successTitle: 'Faleminderit!',
      successMsg: 'Do t\'ju kontaktojmë së shpejti për të aktivizuar provën tuaj falas.',
      nameRequired: 'Emri i biznesit është i detyrueshëm.',
      emailRequired: 'Email-i është i detyrueshëm.',
      emailInvalid: 'Ju lutem vendosni një email të vlefshëm.',
      phoneRequired: 'Numri i telefonit është i detyrueshëm.',
    },
    demo: {
      title: 'Shiko si funksionon',
      subtitle: 'Një demonstrim i shpejtë i platformës Rezervo Online.',
      close: 'Mbyll',
    },
  },
  en: {
    nav: {
      howItWorks: 'How it works',
      faq: 'FAQ',
      contact: 'Contact',
      login: 'Login',
      register: 'Register your business',
    },
    hero: {
      headline1: 'Clients book.',
      headline2: 'You focus on your business.',
      subtitle: 'Automate bookings and manage everything from a single platform.',
      cta1: 'Register your business',
      cta2: 'Watch demo',
      dashboardTitle: 'Dashboard — Studio Elegance',
      dashboardDate: 'Today, Aug 4 2026',
      live: 'Live',
      stats: [
        { label: "Today\'s bookings", value: '12', color: '#7C3AED' },
        { label: 'New clients', value: '5', color: '#10B981' },
        { label: 'Revenue', value: '€480', color: '#F97316' },
      ],
      bookings: [
        { name: 'Arta K.', service: 'Haircut', time: '10:00', status: 'Confirmed' },
        { name: 'Besnik H.', service: 'Coloring', time: '11:30', status: 'Pending' },
        { name: 'Mirela S.', service: 'Manicure', time: '13:00', status: 'Confirmed' },
      ],
    },
    social: {
      label: 'Businesses already saving time',
      stats: [
        { value: '500+', label: 'Active businesses' },
        { value: '12,000+', label: 'Bookings per month' },
        { value: '4.9★', label: 'Average rating' },
        { value: '30 min', label: 'Setup time' },
      ],
    },
    why: {
      badge: 'Why Rezervo Online',
      title: 'Everything your business needs',
      subtitle: 'From automated bookings to client management — all in one platform.',
      cards: [
        { icon: '⚡', title: '24/7 Bookings', desc: 'Clients book any hour of the day, even while you sleep. Zero phone calls, zero messages.', glow: 'rgba(93,10,244,0.2)', border: 'rgba(93,10,244,0.3)' },
        { icon: '📅', title: 'Auto Calendar', desc: 'Every booking appears instantly in your calendar. No conflicts, no wasted time.', glow: 'rgba(16,185,129,0.15)', border: 'rgba(16,185,129,0.25)' },
        { icon: '🔔', title: 'Waitlist', desc: 'When a slot opens up, the system automatically notifies the next client on the list.', glow: 'rgba(249,115,22,0.15)', border: 'rgba(249,115,22,0.25)' },
        { icon: '🔗', title: 'Personal Link', desc: 'Every business gets its own unique link. Share on Instagram, WhatsApp or your bio.', glow: 'rgba(124,58,237,0.15)', border: 'rgba(124,58,237,0.25)' },
      ],
    },
    how: {
      badge: 'How it works',
      title: 'Ready in 4 simple steps',
      steps: [
        { num: '1', title: 'Register your business', desc: 'Create your free account in 2 minutes.', icon: '🏢' },
        { num: '2', title: 'Customize services', desc: 'Add your services, prices and schedules.', icon: '⚙️' },
        { num: '3', title: 'Share your link', desc: 'Share your personal link with clients anywhere.', icon: '🔗' },
        { num: '4', title: 'Receive bookings', desc: 'Clients book themselves — no manual intervention.', icon: '📲' },
      ],
    },
    dashboard: {
      badge: 'Dashboard',
      title1: 'Manage bookings',
      title2: 'from a single dashboard.',
      subtitle: 'View all bookings, manage clients and track revenue — all from one place, in real time.',
      features: ['Real-time bookings', 'Automatic notifications', 'Statistics and reports', 'Staff management'],
      metrics: [
        { label: 'Bookings this week', value: '47', delta: '+12%', color: '#7C3AED' },
        { label: 'Active clients', value: '128', delta: '+8%', color: '#10B981' },
        { label: 'Revenue (month)', value: '€2,340', delta: '+23%', color: '#F97316' },
        { label: 'Fill rate', value: '94%', delta: '+5%', color: '#3B82F6' },
      ],
      calendarLabel: 'Calendar — August 2026',
      upcomingLabel: 'Upcoming bookings',
      upcoming: [
        { name: 'Arta K.', time: '14:00', service: 'Haircut', color: '#7C3AED' },
        { name: 'Gent M.', time: '15:30', service: 'Coloring', color: '#10B981' },
      ],
    },
    link: {
      badge: 'Personal link',
      title1: 'Your personal link.',
      title2: 'Your bookings.',
      subtitle: 'Every business gets its own unique link: rezervo-online.al/your-name. Share on Instagram bio, WhatsApp or anywhere — clients book in 3 clicks.',
      items: ['Share link in Instagram bio', 'Client books within 30 seconds', 'You get notified instantly'],
      services: [
        { name: 'Haircut', price: '€15', time: '45 min' },
        { name: 'Coloring', price: '€35', time: '90 min' },
        { name: 'Manicure', price: '€12', time: '30 min' },
      ],
      bookNow: 'Book now →',
      confirmed: 'Booking confirmed!',
      chooseService: 'Choose a service',
      location: 'Tirana, Albania',
    },
    pricing: {
      badge: 'Pricing',
      title: 'Everything you need.',
      subtitle: 'No surprises. No hidden fees.',
      planLabel: 'Standard plan',
      trialBadge: '30 DAYS FREE',
      perMonth: '/month',
      features: [
        'Unlimited bookings',
        'Waitlist',
        'Full dashboard',
        'Unique personal link',
        'Automatic notifications',
        '24/7 SUPPORT',
      ],
      cta: 'Register your business →',
      note: 'No credit card • Cancel anytime',
    },
    testimonials: {
      badge: 'What businesses say',
      title: 'Real businesses. Real results.',
      items: [
        { name: 'Arta Kelmendi', role: 'Studio Elegance, Tirana', text: 'Since I started using Rezervo Online, I no longer get calls for bookings. Clients book themselves and I focus on my work.', avatar: 'A' },
        { name: 'Besnik Hoxha', role: 'Barbershop Hoxha, Durrës', text: 'The automatic calendar has saved me over 2 hours a day. Now I can accept 30% more clients without extra stress.', avatar: 'B' },
        { name: 'Mirela Shehu', role: 'Beauty Center Mirela, Vlorë', text: 'The waitlist is fantastic. When someone cancels, the slot fills automatically. Zero lost revenue.', avatar: 'M' },
      ],
    },
    faq: {
      badge: 'FAQ',
      title: 'Frequently asked questions',
      subtitle: 'Everything you need to know before you start.',
      items: [
        { question: 'How does Rezervo Online work?', answer: 'Register your business, customize your services and schedules, and share your personal link with clients. They book themselves — you get notified instantly and the booking appears automatically in your dashboard.' },
        { question: 'Do I need technical knowledge?', answer: 'Not at all. The platform is designed for anyone to use. Setup takes about 10 minutes and requires no programming knowledge.' },
        { question: 'What happens if a client cancels?', answer: 'The client can cancel or change the booking according to the policy you set. You get notified immediately and the slot is automatically freed in the calendar.' },
        { question: 'Can I have multiple services and staff?', answer: 'Yes. You can add multiple services, assign different staff to each service, and manage their schedules from one place.' },
        { question: 'How does the waitlist work?', answer: 'When a slot is taken, clients can register on the waitlist. Also, when a client books, the platform asks "Would you like to be notified if an earlier slot becomes available?" If they click "YES", when someone cancels, the system automatically notifies the people on the list.' },
        { question: 'Can I try it for free?', answer: 'Yes, we offer a 30-day free trial with no credit card required. After the trial period, you can continue with the 27€/month plan.' },
      ],
    },
    cta: {
      title1: 'Ready to automate',
      title2: 'your bookings?',
      subtitle: 'Join hundreds of businesses already saving time and growing their bookings.',
      cta: 'Register your business',
      note: '30 days free • No credit card • Cancel anytime',
    },
    footer: {
      tagline: 'The booking platform for Albanian businesses.',
      links: [
        { label: 'Instagram', href: 'https://instagram.com' },
        { label: 'Facebook', href: 'https://facebook.com' },
        { label: 'Contact', href: '#footer' },
        { label: 'Terms', href: '/terms' },
        { label: 'Privacy', href: '/privacy' },
      ],
      copy: '© 2026 Rezervo Online. All rights reserved.',
    },
    modal: {
      title: 'Register your business',
      subtitle: '30 days free • No credit card • Cancel anytime',
      namePlaceholder: 'Business name',
      emailPlaceholder: 'Business email',
      phonePlaceholder: 'Phone number',
      submit: 'Start free trial →',
      submitting: 'Sending...',
      successTitle: 'Thank you!',
      successMsg: 'We will contact you shortly to activate your free trial.',
      nameRequired: 'Business name is required.',
      emailRequired: 'Email is required.',
      emailInvalid: 'Please enter a valid email.',
      phoneRequired: 'Phone number is required.',
    },
    demo: {
      title: 'See how it works',
      subtitle: 'A quick walkthrough of the Rezervo Online platform.',
      close: 'Close',
    },
  },
};

// ─── Demo Video Modal ─────────────────────────────────────────────────────────
function DemoModal({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const { lang } = useLang();
  const tr = t[lang].demo;

  useEffect(() => {
    if (isOpen) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = '';
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center px-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="absolute inset-0 bg-[rgba(4,2,12,0.9)] backdrop-blur-sm" />
      <div
        className="relative w-full max-w-3xl rounded-3xl overflow-hidden"
        style={{
          background: 'linear-gradient(145deg, rgba(20,12,45,0.98) 0%, rgba(10,6,25,0.99) 100%)',
          border: '1px solid rgba(93,10,244,0.4)',
          boxShadow: '0 40px 100px rgba(0,0,0,0.7), 0 0 60px rgba(93,10,244,0.15)',
        }}
      >
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-96 h-24 bg-[rgba(93,10,244,0.15)] rounded-full blur-[60px] pointer-events-none" />
        <div className="relative z-10 p-6">
          <div className="flex items-center justify-between mb-5">
            <div>
              <h2 className="text-xl font-bold text-white">{tr.title}</h2>
              <p className="text-[#6B6080] text-xs mt-0.5">{tr.subtitle}</p>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-full flex items-center justify-center text-[#6B6080] hover:text-white hover:bg-[rgba(255,255,255,0.08)] transition-all duration-200"
              aria-label="Close demo"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Demo screen mockup */}
          <div
            className="rounded-2xl overflow-hidden"
            style={{ border: '1px solid rgba(93,10,244,0.25)', background: 'rgba(12,9,24,0.9)' }}
          >
            {/* Browser bar */}
            <div className="flex items-center gap-2 px-4 py-3 border-b border-[rgba(93,10,244,0.15)] bg-[rgba(10,8,20,0.6)]">
              <div className="w-3 h-3 rounded-full bg-[#FF5F57]" />
              <div className="w-3 h-3 rounded-full bg-[#FEBC2E]" />
              <div className="w-3 h-3 rounded-full bg-[#28C840]" />
              <div className="flex-1 mx-4 h-6 rounded-lg bg-[rgba(93,10,244,0.1)] border border-[rgba(93,10,244,0.2)] flex items-center px-3">
                <span className="text-xs text-[#6B6080]">rezervo-online.al/studio-elegance</span>
              </div>
            </div>

            {/* Demo content — animated booking flow */}
            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6 min-h-[320px]">
              {/* Left: booking page */}
              <div className="flex flex-col gap-3">
                <div className="text-[#A89EC4] text-xs font-semibold uppercase tracking-wider mb-1">
                  {lang === 'sq' ? 'Faqja e rezervimit (klienti)' : 'Booking page (client)'}
                </div>
                <div className="rounded-xl p-4" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#5D0AF4] to-[#7C3AED] flex items-center justify-center text-base">✂️</div>
                    <div>
                      <div className="text-white text-sm font-semibold">Studio Elegance</div>
                      <div className="text-[#6B6080] text-xs">{lang === 'sq' ? 'Tiranë, Shqipëri' : 'Tirana, Albania'}</div>
                    </div>
                  </div>
                  {[
                    { name: lang === 'sq' ? 'Prerje flokësh' : 'Haircut', price: '€15', time: '45 min' },
                    { name: lang === 'sq' ? 'Ngjyrosje' : 'Coloring', price: '€35', time: '90 min' },
                  ].map((s, i) => (
                    <div key={i} className={`flex items-center justify-between rounded-lg px-3 py-2.5 mb-2 cursor-pointer transition-all ${i === 0 ? 'border-[rgba(93,10,244,0.5)] bg-[rgba(93,10,244,0.15)]' : 'bg-[rgba(255,255,255,0.02)]'}`}
                      style={{ border: i === 0 ? '1px solid rgba(93,10,244,0.5)' : '1px solid rgba(255,255,255,0.05)' }}>
                      <div>
                        <div className="text-white text-xs font-medium">{s.name}</div>
                        <div className="text-[#6B6080] text-xs">{s.time}</div>
                      </div>
                      <span className="text-[#B89EF8] text-xs font-semibold">{s.price}</span>
                    </div>
                  ))}
                  <div className="mt-3 w-full py-2.5 rounded-xl text-xs font-semibold text-white text-center" style={{ background: 'linear-gradient(135deg, #5D0AF4, #7C3AED)' }}>
                    {lang === 'sq' ? 'Rezervo tani →' : 'Book now →'}
                  </div>
                </div>
              </div>

              {/* Right: dashboard notification */}
              <div className="flex flex-col gap-3">
                <div className="text-[#A89EC4] text-xs font-semibold uppercase tracking-wider mb-1">
                  {lang === 'sq' ? 'Dashboard (biznesi)' : 'Dashboard (business)'}
                </div>
                <div className="rounded-xl p-4" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-white text-xs font-semibold">Rezervo Dashboard</span>
                    <span className="text-[#10B981] text-xs">● Live</span>
                  </div>
                  <div className="flex flex-col gap-2">
                    {[
                      { name: 'Arta K.', service: lang === 'sq' ? 'Prerje flokësh' : 'Haircut', time: '10:00', status: lang === 'sq' ? 'Konfirmuar' : 'Confirmed', color: '#10B981' },
                      { name: 'Besnik H.', service: lang === 'sq' ? 'Ngjyrosje' : 'Coloring', time: '11:30', status: lang === 'sq' ? 'Në pritje' : 'Pending', color: '#F97316' },
                      { name: 'Mirela S.', service: lang === 'sq' ? 'Manikyr' : 'Manicure', time: '13:00', status: lang === 'sq' ? 'Konfirmuar' : 'Confirmed', color: '#10B981' },
                    ].map((b, i) => (
                      <div key={i} className="flex items-center justify-between rounded-lg px-3 py-2" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.05)' }}>
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 rounded-full bg-[rgba(93,10,244,0.3)] flex items-center justify-center text-xs text-[#B89EF8] font-semibold">{b.name[0]}</div>
                          <div>
                            <div className="text-white text-xs font-medium">{b.name}</div>
                            <div className="text-[#6B6080] text-xs">{b.service}</div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-[#A89EC4] text-xs">{b.time}</span>
                          <span className="text-xs px-1.5 py-0.5 rounded-full" style={{ background: `${b.color}22`, color: b.color }}>{b.status}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-3 p-2.5 rounded-lg flex items-center gap-2" style={{ background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.2)' }}>
                    <span className="text-[#10B981] text-sm">🔔</span>
                    <span className="text-[#10B981] text-xs">{lang === 'sq' ? 'Rezervim i ri nga Arta K.' : 'New booking from Arta K.'}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-center mt-5">
            <button
              onClick={onClose}
              className="px-6 py-2.5 rounded-xl text-sm font-medium border border-[rgba(93,10,244,0.35)] text-[#C4B5FD] hover:border-[rgba(93,10,244,0.6)] hover:bg-[rgba(93,10,244,0.08)] transition-all duration-200"
            >
              {tr.close}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Registration Modal ───────────────────────────────────────────────────────
function RegistrationModal({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const { lang } = useLang();
  const tr = t[lang].modal;
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [errors, setErrors] = useState<{ name?: string; email?: string; phone?: string }>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  const validate = () => {
    const newErrors: { name?: string; email?: string; phone?: string } = {};
    if (!name.trim()) newErrors.name = tr.nameRequired;
    if (!email.trim()) newErrors.email = tr.emailRequired;
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) newErrors.email = tr.emailInvalid;
    if (!phone.trim()) newErrors.phone = tr.phoneRequired;
    return newErrors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    setErrors({});
    setSubmitting(true);
    try {
      const res = await fetch('/api/register-business', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ businessName: name, email, phone }),
      });
      if (!res.ok) {
        const data = await res.json();
        console.error('Registration error:', data.error);
      }
    } catch (err) {
      console.error('Network error:', err);
    } finally {
      setSubmitting(false);
      setSubmitted(true);
    }
  };

  const handleClose = () => {
    onClose();
    setTimeout(() => {
      setName(''); setEmail(''); setPhone('');
      setErrors({}); setSubmitting(false); setSubmitted(false);
    }, 300);
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center px-4"
      onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}
    >
      <div className="absolute inset-0 bg-[rgba(4,2,12,0.85)] backdrop-blur-sm" />
      <div
        className="relative w-full max-w-md rounded-3xl overflow-hidden"
        style={{
          background: 'linear-gradient(145deg, rgba(20,12,45,0.98) 0%, rgba(10,6,25,0.99) 100%)',
          border: '1px solid rgba(93,10,244,0.4)',
          boxShadow: '0 40px 100px rgba(0,0,0,0.7), 0 0 60px rgba(93,10,244,0.15)',
        }}
      >
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-64 h-32 bg-[rgba(93,10,244,0.2)] rounded-full blur-[60px] pointer-events-none" />
        <div className="relative z-10 p-8">
          <button
            onClick={handleClose}
            className="absolute top-5 right-5 w-8 h-8 rounded-full flex items-center justify-center text-[#6B6080] hover:text-white hover:bg-[rgba(255,255,255,0.08)] transition-all duration-200"
            aria-label="Close"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {submitted ? (
            <div className="text-center py-6">
              <div className="w-16 h-16 rounded-full mx-auto mb-5 flex items-center justify-center" style={{ background: 'rgba(93,10,244,0.25)', border: '1px solid rgba(93,10,244,0.5)' }}>
                <svg className="w-8 h-8 text-[#B89EF8]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-white mb-3">{tr.successTitle}</h3>
              <p className="text-[#8B7FAA] text-sm leading-relaxed">{tr.successMsg}</p>
            </div>
          ) : (
            <>
              <div className="mb-7">
                <h2 className="text-2xl font-bold text-white mb-1.5">{tr.title}</h2>
                <p className="text-[#6B6080] text-xs">{tr.subtitle}</p>
              </div>
              <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-4">
                <div>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => { setName(e.target.value); if (errors.name) setErrors((p) => ({ ...p, name: undefined })); }}
                    placeholder={tr.namePlaceholder}
                    className="w-full px-4 py-3.5 rounded-xl text-sm text-white placeholder-[#4A4060] outline-none transition-all duration-200"
                    style={{ background: 'rgba(255,255,255,0.04)', border: errors.name ? '1px solid rgba(239,68,68,0.6)' : '1px solid rgba(93,10,244,0.25)' }}
                    onFocus={(e) => { e.currentTarget.style.border = '1px solid rgba(93,10,244,0.6)'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(93,10,244,0.1)'; }}
                    onBlur={(e) => { e.currentTarget.style.border = errors.name ? '1px solid rgba(239,68,68,0.6)' : '1px solid rgba(93,10,244,0.25)'; e.currentTarget.style.boxShadow = 'none'; }}
                  />
                  {errors.name && <p className="text-[#F87171] text-xs mt-1.5 pl-1">{errors.name}</p>}
                </div>
                <div>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); if (errors.email) setErrors((p) => ({ ...p, email: undefined })); }}
                    placeholder={tr.emailPlaceholder}
                    className="w-full px-4 py-3.5 rounded-xl text-sm text-white placeholder-[#4A4060] outline-none transition-all duration-200"
                    style={{ background: 'rgba(255,255,255,0.04)', border: errors.email ? '1px solid rgba(239,68,68,0.6)' : '1px solid rgba(93,10,244,0.25)' }}
                    onFocus={(e) => { e.currentTarget.style.border = '1px solid rgba(93,10,244,0.6)'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(93,10,244,0.1)'; }}
                    onBlur={(e) => { e.currentTarget.style.border = errors.email ? '1px solid rgba(239,68,68,0.6)' : '1px solid rgba(93,10,244,0.25)'; e.currentTarget.style.boxShadow = 'none'; }}
                  />
                  {errors.email && <p className="text-[#F87171] text-xs mt-1.5 pl-1">{errors.email}</p>}
                </div>
                <div>
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => { setPhone(e.target.value); if (errors.phone) setErrors((p) => ({ ...p, phone: undefined })); }}
                    placeholder={tr.phonePlaceholder}
                    className="w-full px-4 py-3.5 rounded-xl text-sm text-white placeholder-[#4A4060] outline-none transition-all duration-200"
                    style={{ background: 'rgba(255,255,255,0.04)', border: errors.phone ? '1px solid rgba(239,68,68,0.6)' : '1px solid rgba(93,10,244,0.25)' }}
                    onFocus={(e) => { e.currentTarget.style.border = '1px solid rgba(93,10,244,0.6)'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(93,10,244,0.1)'; }}
                    onBlur={(e) => { e.currentTarget.style.border = errors.phone ? '1px solid rgba(239,68,68,0.6)' : '1px solid rgba(93,10,244,0.25)'; e.currentTarget.style.boxShadow = 'none'; }}
                  />
                  {errors.phone && <p className="text-[#F87171] text-xs mt-1.5 pl-1">{errors.phone}</p>}
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full py-4 rounded-2xl text-base font-semibold text-white transition-all duration-200 hover:opacity-90 disabled:opacity-60 mt-1"
                  style={{ background: 'linear-gradient(135deg, #5D0AF4, #7C3AED)', boxShadow: '0 8px 32px rgba(93,10,244,0.45)' }}
                >
                  {submitting ? tr.submitting : tr.submit}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Navbar ──────────────────────────────────────────────────────────────────
function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { lang, setLang } = useLang();
  const { openModal } = useModal();
  const tr = t[lang].nav;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <nav
      // Scrolled background is now a fully opaque literal hex (#060510,
      // this app's own base page background — see <main>'s style prop
      // and every section's radial-gradient dark stop) rather than
      // rgba(10,8,20,0.92): a 92%-alpha color still lets 8% of whatever
      // is scrolled underneath bleed through, which on a real device can
      // read as "not quite dark enough" rather than a clearly solid
      // header. A literal hex value has no alpha channel at all, so
      // there's nothing left to look transparent regardless of what's
      // behind it. backdrop-blur-xl/border are otherwise unchanged.
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-[#060510] backdrop-blur-xl border-b border-[rgba(93,10,244,0.15)]'
          : 'bg-[rgba(6,5,16,0.50)] backdrop-blur-xl'
      }`}
    >
      <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
        <a href="#hero" className="flex items-center">
          <Image
            src="/assets/images/LOGO_REZERVO_HORIZONTALE-removebg-preview-1785868391038.png"
            alt="Rezervo Online Logo"
            width={146}
            height={80}
            className="h-20 w-auto object-contain"
            priority
          />
        </a>

        <div className="hidden md:flex items-center gap-8">
          {[
            { label: tr.howItWorks, href: '#si-funksionon' },
            { label: tr.faq, href: '#faq' },
            { label: tr.contact, href: '/contact' },
          ].map((item) => (
            <a
              key={item.label}
              href={item.href}
              className="text-sm text-[#A89EC4] hover:text-white transition-colors duration-200 font-medium"
            >
              {item.label}
            </a>
          ))}
          <a
            href="/login"
            className="text-sm text-[#A89EC4] hover:text-white transition-colors duration-200 font-medium"
          >
            {tr.login}
          </a>
        </div>

        <div className="hidden md:flex items-center gap-3">
          <div className="flex items-center rounded-xl overflow-hidden border border-[rgba(93,10,244,0.3)] bg-[rgba(93,10,244,0.08)]">
            <button
              onClick={() => setLang('sq')}
              className={`px-3 py-1.5 text-xs font-semibold transition-all duration-200 ${lang === 'sq' ? 'bg-[rgba(93,10,244,0.5)] text-white' : 'text-[#A89EC4] hover:text-white'}`}
            >
              SQ
            </button>
            <button
              onClick={() => setLang('en')}
              className={`px-2.5 py-1 text-xs font-semibold transition-all duration-200 ${lang === 'en' ? 'bg-[rgba(93,10,244,0.5)] text-white' : 'text-[#A89EC4] hover:text-white'}`}
            >
              EN
            </button>
          </div>
          <a
            href="/register"
            className="px-5 py-2.5 rounded-xl text-sm font-semibold bg-gradient-to-r from-[#5D0AF4] to-[#7C3AED] text-white hover:opacity-90 transition-all duration-200 shadow-[0_4px_20px_rgba(93,10,244,0.4)]"
          >
            {tr.register}
          </a>
        </div>

        <div className="md:hidden flex items-center gap-3 relative z-[60]">
          <div className="flex items-center rounded-lg overflow-hidden border border-[rgba(93,10,244,0.3)] bg-[rgba(93,10,244,0.08)]">
            <button
              onClick={() => setLang('sq')}
              className={`px-2.5 py-1 text-xs font-semibold transition-all duration-200 ${lang === 'sq' ? 'bg-[rgba(93,10,244,0.5)] text-white' : 'text-[#A89EC4]'}`}
            >
              SQ
            </button>
            <button
              onClick={() => setLang('en')}
              className={`px-2.5 py-1 text-xs font-semibold transition-all duration-200 ${lang === 'en' ? 'bg-[rgba(93,10,244,0.5)] text-white' : 'text-[#A89EC4]'}`}
            >
              EN
            </button>
          </div>
          {/* min-w/min-h + flex-centering guarantees a >=44x44px tap
              target (was ~36x34px from padding alone) without touching
              the bars themselves — same size, same gaps, same colors,
              just centered inside a larger invisible hit area. */}
          <button
            className="text-white p-2 min-w-[44px] min-h-[44px] flex flex-col items-center justify-center"
            onClick={() => setMenuOpen((prev) => !prev)}
            aria-label="Toggle menu"
          >
            <div className="w-5 h-0.5 bg-white mb-1.5 transition-all" />
            <div className="w-5 h-0.5 bg-white mb-1.5 transition-all" />
            <div className="w-5 h-0.5 bg-white transition-all" />
          </button>
        </div>
      </div>

      {menuOpen && (
        // Invisible tap-catcher, not a visual backdrop (no background/
        // opacity) — z-40, strictly below the mobile controls row above
        // (z-60) so the hamburger button always stays on top and
        // clickable, and below the menu panel below (z-50) so its own
        // links remain tappable. Its only job is to let a tap anywhere
        // else on the page (Hero content, etc.) close the menu, since
        // nothing previously did that.
        <div
          className="md:hidden fixed inset-0 z-40"
          onClick={() => setMenuOpen(false)}
          aria-hidden="true"
        />
      )}

      {menuOpen && (
        <div className="md:hidden relative z-50 bg-[rgba(10,8,20,0.97)] border-t border-[rgba(93,10,244,0.15)] px-6 py-4 flex flex-col gap-4">
          {[
            { label: tr.howItWorks, href: '#si-funksionon' },
            { label: tr.faq, href: '#faq' },
            { label: tr.contact, href: '/contact' },
            { label: tr.login, href: '/login' },
          ].map((item) => (
            <a
              key={item.label}
              href={item.href}
              className="text-sm text-[#A89EC4] hover:text-white transition-colors"
              onClick={() => setMenuOpen(false)}
            >
              {item.label}
            </a>
          ))}
          <a
            href="/register"
            className="px-5 py-2.5 rounded-xl text-sm font-semibold bg-gradient-to-r from-[#5D0AF4] to-[#7C3AED] text-white text-center"
            onClick={() => setMenuOpen(false)}
          >
            {tr.register}
          </a>
        </div>
      )}
    </nav>
  );
}

// ─── Section 1: Hero ─────────────────────────────────────────────────────────
function HeroSection({ onOpenDemo }: { onOpenDemo: () => void }) {
  const { lang } = useLang();
  const { openModal } = useModal();
  const tr = t[lang].hero;

  return (
    <section
      id="hero"
      className="relative min-h-screen flex flex-col items-center justify-center pt-24 pb-16 px-6 overflow-hidden"
    >
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,_#2D0A5E_0%,_#0D0B1E_40%,_#060510_100%)] pointer-events-none" />
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[rgba(93,10,244,0.12)] rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-[rgba(60,20,180,0.08)] rounded-full blur-[100px] pointer-events-none" />

      <div className="relative z-10 max-w-5xl mx-auto text-center">
        <div className="flex justify-center mb-8">
          <Image
            src="/assets/images/LOGO_REZERVO_HORIZONTALE-removebg-preview-1785868391038.png"
            alt="Rezervo Online Logo"
            width={306}
            height={168}
            className="h-[168px] w-auto object-contain"
            priority
          />
        </div>

        <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold leading-[1.1] tracking-tight mb-6">
          <span className="text-white">{tr.headline1}</span>
          <br />
          <span
            style={{
              background: 'linear-gradient(135deg, #8B5CF6 0%, #6D28D9 40%, #A78BFA 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            {tr.headline2}
          </span>
        </h1>

        <p className="text-base text-[#8B7FAA] max-w-xl mx-auto mb-10 leading-relaxed">
          {tr.subtitle}
        </p>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16">
          <a
            href="/register"
            className="group px-8 py-4 rounded-2xl text-base font-semibold bg-gradient-to-r from-[#5D0AF4] to-[#7C3AED] text-white hover:opacity-90 transition-all duration-200 shadow-[0_8px_32px_rgba(93,10,244,0.45)] flex items-center gap-2"
          >
            {tr.cta1}
            <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </a>
          <button
            onClick={onOpenDemo}
            className="px-8 py-4 rounded-2xl text-base font-semibold border border-[rgba(93,10,244,0.35)] text-[#C4B5FD] hover:border-[rgba(93,10,244,0.6)] hover:bg-[rgba(93,10,244,0.08)] transition-all duration-200 flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {tr.cta2}
          </button>
        </div>

      </div>
    </section>
  );
}

// ─── Section 2: Why ───────────────────────────────────────────────────────────
function WhySection() {
  const { lang } = useLang();
  const tr = t[lang].why;
  const { ref, visible } = useScrollReveal();

  return (
    <section className="relative py-24 px-6 overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_#0D0B1E_0%,_#060510_100%)]" />
      <div
        ref={ref}
        className="relative z-10 max-w-5xl mx-auto transition-all duration-700"
        style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(30px)' }}
      >
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[rgba(93,10,244,0.3)] bg-[rgba(93,10,244,0.08)] text-[#B89EF8] text-xs font-medium mb-5">
            {tr.badge}
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-white leading-tight">{tr.title}</h2>
          <p className="text-[#6B6080] mt-3 max-w-lg mx-auto text-base">{tr.subtitle}</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {tr.cards.map((card) => (
            <div
              key={card.title}
              className="relative rounded-2xl p-6 transition-all duration-300 hover:-translate-y-1 cursor-default"
              style={{
                background: 'linear-gradient(135deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.02) 100%)',
                border: `1px solid ${card.border}`,
                backdropFilter: 'blur(16px)',
                boxShadow: `0 8px 32px ${card.glow}, inset 0 1px 0 rgba(255,255,255,0.06)`,
              }}
            >
              <div className="text-3xl mb-4">{card.icon}</div>
              <h3 className="text-white font-semibold text-base mb-2">{card.title}</h3>
              <p className="text-[#7A7090] text-sm leading-relaxed">{card.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Section 3: How It Works ──────────────────────────────────────────────────
function HowItWorksSection() {
  const { lang } = useLang();
  const tr = t[lang].how;
  const { ref, visible } = useScrollReveal();

  return (
    <section id="si-funksionon" className="relative py-24 px-6 overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_#1A0A3E_0%,_#060510_60%)]" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[300px] bg-[rgba(93,10,244,0.06)] rounded-full blur-[100px] pointer-events-none" />
      <div
        ref={ref}
        className="relative z-10 max-w-5xl mx-auto transition-all duration-700"
        style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(30px)' }}
      >
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[rgba(93,10,244,0.3)] bg-[rgba(93,10,244,0.08)] text-[#B89EF8] text-xs font-medium mb-5">
            {tr.badge}
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-white">{tr.title}</h2>
        </div>
        <div className="flex flex-col md:flex-row items-center justify-center gap-0">
          {tr.steps.map((step, i) => (
            <React.Fragment key={step.num}>
              <div className="flex flex-col items-center text-center max-w-[180px]">
                <div
                  className="w-16 h-16 rounded-2xl flex items-center justify-center text-2xl mb-4 relative"
                  style={{
                    background: 'linear-gradient(135deg, rgba(93,10,244,0.25) 0%, rgba(124,58,237,0.15) 100%)',
                    border: '1px solid rgba(93,10,244,0.4)',
                    boxShadow: '0 8px 24px rgba(93,10,244,0.2)',
                  }}
                >
                  {step.icon}
                  <div
                    className="absolute -top-2 -right-2 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white"
                    style={{ background: 'linear-gradient(135deg, #5D0AF4, #7C3AED)' }}
                  >
                    {step.num}
                  </div>
                </div>
                <h3 className="text-white font-semibold text-sm mb-1.5">{step.title}</h3>
                <p className="text-[#6B6080] text-xs leading-relaxed">{step.desc}</p>
              </div>
              {i < tr.steps.length - 1 && (
                <div className="flex items-center justify-center mx-4 my-4 md:my-0 rotate-90 md:rotate-0">
                  <svg width="32" height="16" viewBox="0 0 32 16" fill="none">
                    <path d="M0 8h28M22 2l8 6-8 6" stroke="rgba(93,10,244,0.5)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
              )}
            </React.Fragment>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Section 4: Dashboard ─────────────────────────────────────────────────────
function DashboardSection() {
  const { lang } = useLang();
  const tr = t[lang].dashboard;
  const { ref, visible } = useScrollReveal();

  return (
    <section className="relative py-24 px-6 overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,_#130830_0%,_#060510_60%)]" />
      <div className="absolute right-1/4 top-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-[rgba(93,10,244,0.1)] rounded-full blur-[120px] pointer-events-none" />
      <div
        ref={ref}
        className="relative z-10 max-w-5xl mx-auto transition-all duration-700"
        style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(30px)' }}
      >
        <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16">
          <div className="flex-1 max-w-md">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[rgba(93,10,244,0.3)] bg-[rgba(93,10,244,0.08)] text-[#B89EF8] text-xs font-medium mb-6">
              {tr.badge}
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-white leading-tight mb-5">
              {tr.title1}
              <br />
              <span style={{ background: 'linear-gradient(135deg, #8B5CF6 0%, #A78BFA 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
                {tr.title2}
              </span>
            </h2>
            <p className="text-[#7A7090] text-base leading-relaxed mb-8">{tr.subtitle}</p>
            <ul className="flex flex-col gap-3">
              {tr.features.map((item) => (
                <li key={item} className="flex items-center gap-3 text-[#A89EC4] text-sm">
                  <div className="w-5 h-5 rounded-full bg-[rgba(93,10,244,0.25)] border border-[rgba(93,10,244,0.4)] flex items-center justify-center flex-shrink-0">
                    <svg className="w-2.5 h-2.5 text-[#B89EF8]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  {item}
                </li>
              ))}
            </ul>
          </div>
          <div className="flex-1 w-full max-w-lg">
            <div className="rounded-2xl overflow-hidden" style={{ border: '1px solid rgba(93,10,244,0.25)', background: 'rgba(12,9,24,0.9)', boxShadow: '0 32px 80px rgba(0,0,0,0.5), 0 0 60px rgba(93,10,244,0.12)' }}>
              <div className="flex items-center justify-between px-5 py-3.5 border-b border-[rgba(93,10,244,0.12)] bg-[rgba(10,8,20,0.5)]">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-[#5D0AF4]" />
                  <span className="text-white text-xs font-semibold">Rezervo Dashboard</span>
                </div>
                <div className="px-2 py-0.5 rounded-full bg-[rgba(16,185,129,0.15)] border border-[rgba(16,185,129,0.25)] text-[#10B981] text-xs">● Live</div>
              </div>
              <div className="p-5 flex flex-col gap-4">
                <div className="grid grid-cols-2 gap-3">
                  {tr.metrics.map((m) => (
                    <div key={m.label} className="rounded-xl p-3.5" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                      <div className="text-xl font-bold" style={{ color: m.color }}>{m.value}</div>
                      <div className="text-[#6B6080] text-xs mt-0.5">{m.label}</div>
                      <div className="text-[#10B981] text-xs mt-1 font-medium">{m.delta} ↑</div>
                    </div>
                  ))}
                </div>
                <div className="rounded-xl p-3.5" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                  <div className="text-[#A89EC4] text-xs font-medium mb-3">{tr.calendarLabel}</div>
                  <div className="grid grid-cols-7 gap-1">
                    {['H', 'M', 'M', 'E', 'P', 'Sh', 'D'].map((d, i) => (
                      <div key={i} className="text-center text-[#4A4060] text-xs py-1">{d}</div>
                    ))}
                    {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
                      <div key={d} className={`text-center text-xs py-1 rounded-lg ${d === 4 ? 'bg-[#5D0AF4] text-white font-semibold' : [7, 12, 15, 19, 22].includes(d) ? 'bg-[rgba(93,10,244,0.2)] text-[#B89EF8]' : 'text-[#6B6080]'}`}>{d}</div>
                    ))}
                  </div>
                </div>
                <div className="flex flex-col gap-2">
                  <div className="text-[#A89EC4] text-xs font-medium">{tr.upcomingLabel}</div>
                  {tr.upcoming.map((r) => (
                    <div key={r.name} className="flex items-center justify-between rounded-lg px-3 py-2" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.05)' }}>
                      <div className="flex items-center gap-2.5">
                        <div className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white" style={{ background: r.color }}>{r.name[0]}</div>
                        <div>
                          <div className="text-white text-xs font-medium">{r.name}</div>
                          <div className="text-[#6B6080] text-xs">{r.service}</div>
                        </div>
                      </div>
                      <span className="text-[#A89EC4] text-xs font-medium">{r.time}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Section 5: Personal Link ─────────────────────────────────────────────────
function PersonalLinkSection() {
  const { lang } = useLang();
  const tr = t[lang].link;
  const { ref, visible } = useScrollReveal();

  return (
    <section className="relative py-24 px-6 overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,_#0D0B1E_0%,_#060510_100%)]" />
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[600px] bg-[rgba(93,10,244,0.08)] rounded-full blur-[100px] pointer-events-none" />
      <div
        ref={ref}
        className="relative z-10 max-w-5xl mx-auto transition-all duration-700"
        style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(30px)' }}
      >
        <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20">
          <div className="flex-shrink-0 flex flex-col items-center">
            <div className="flex items-center gap-2 px-4 py-2 rounded-full mb-6" style={{ background: 'linear-gradient(135deg, rgba(131,58,180,0.2), rgba(253,29,29,0.15), rgba(252,176,69,0.15))', border: '1px solid rgba(131,58,180,0.3)' }}>
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="url(#ig-grad)">
                <defs>
                  <linearGradient id="ig-grad" x1="0%" y1="100%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#f09433" />
                    <stop offset="25%" stopColor="#e6683c" />
                    <stop offset="50%" stopColor="#dc2743" />
                    <stop offset="75%" stopColor="#cc2366" />
                    <stop offset="100%" stopColor="#bc1888" />
                  </linearGradient>
                </defs>
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
              </svg>
              <span className="text-white text-xs font-medium">Studio Elegance</span>
            </div>
            <div className="flex flex-col items-center gap-1 mb-4">
              <div className="w-px h-8 bg-gradient-to-b from-[rgba(93,10,244,0.5)] to-transparent" />
              <svg className="w-4 h-4 text-[#5D0AF4]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
            <div className="relative w-[220px] rounded-[36px] overflow-hidden" style={{ background: '#0A0814', border: '2px solid rgba(93,10,244,0.35)', boxShadow: '0 32px 80px rgba(0,0,0,0.6), 0 0 60px rgba(93,10,244,0.15)', padding: '12px' }}>
              <div className="w-16 h-5 bg-[#0A0814] rounded-full mx-auto mb-3 border border-[rgba(255,255,255,0.08)]" />
              <div className="rounded-2xl overflow-hidden" style={{ background: '#0D0B1E' }}>
                <div className="px-4 py-4 text-center border-b border-[rgba(93,10,244,0.15)]">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#5D0AF4] to-[#7C3AED] mx-auto mb-2 flex items-center justify-center text-lg">✂️</div>
                  <div className="text-white text-xs font-semibold">Studio Elegance</div>
                  <div className="text-[#6B6080] text-xs mt-0.5">{tr.location}</div>
                </div>
                <div className="px-3 py-2 border-b border-[rgba(93,10,244,0.1)]">
                  <div className="flex items-center gap-1.5 bg-[rgba(93,10,244,0.1)] rounded-lg px-2 py-1.5">
                    <svg className="w-2.5 h-2.5 text-[#5D0AF4]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                    </svg>
                    <span className="text-[#8B7FAA] text-[9px]">rezervo-online.al/studio-elegance</span>
                  </div>
                </div>
                <div className="px-3 py-3 flex flex-col gap-2">
                  <div className="text-[#A89EC4] text-xs font-medium mb-1">{tr.chooseService}</div>
                  {tr.services.map((s) => (
                    <div key={s.name} className="flex items-center justify-between rounded-lg px-2.5 py-2" style={{ background: 'rgba(93,10,244,0.1)', border: '1px solid rgba(93,10,244,0.2)' }}>
                      <div>
                        <div className="text-white text-xs font-medium">{s.name}</div>
                        <div className="text-[#6B6080] text-xs">{s.time}</div>
                      </div>
                      <span className="text-[#B89EF8] text-xs font-semibold">{s.price}</span>
                    </div>
                  ))}
                  <button className="w-full mt-2 py-2.5 rounded-xl text-xs font-semibold text-white" style={{ background: 'linear-gradient(135deg, #5D0AF4, #7C3AED)' }}>
                    {tr.bookNow}
                  </button>
                </div>
              </div>
            </div>
            <div className="flex flex-col items-center gap-1 mt-4">
              <div className="w-px h-8 bg-gradient-to-b from-[rgba(93,10,244,0.5)] to-transparent" />
              <div className="flex items-center gap-2 px-4 py-2 rounded-full" style={{ background: 'rgba(16,185,129,0.15)', border: '1px solid rgba(16,185,129,0.3)' }}>
                <span className="text-[#10B981] text-xs">✓</span>
                <span className="text-[#10B981] text-xs font-medium">{tr.confirmed}</span>
              </div>
            </div>
          </div>
          <div className="flex-1 max-w-md">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[rgba(93,10,244,0.3)] bg-[rgba(93,10,244,0.08)] text-[#B89EF8] text-xs font-medium mb-6">
              {tr.badge}
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-white leading-tight mb-5">
              {tr.title1}
              <br />
              <span style={{ background: 'linear-gradient(135deg, #8B5CF6 0%, #A78BFA 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
                {tr.title2}
              </span>
            </h2>
            <p className="text-[#7A7090] text-base leading-relaxed mb-6">{tr.subtitle}</p>
            <div className="flex flex-col gap-3">
              {tr.items.map((item, i) => (
                <div key={i} className="flex items-center gap-3 text-[#A89EC4] text-sm">
                  <span className="text-base">{['📱', '⚡', '🔔'][i]}</span>
                  {item}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Section 6: Pricing ───────────────────────────────────────────────────────
function PricingSection() {
  const { lang } = useLang();
  const { openModal } = useModal();
  const tr = t[lang].pricing;
  const { ref, visible } = useScrollReveal();

  return (
    <section id="pricing" className="relative py-24 px-6 overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_#0D0B1E_0%,_#060510_100%)]" />
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div className="w-[500px] h-[500px] bg-[rgba(93,10,244,0.07)] rounded-full blur-[120px]" />
      </div>
      <div
        ref={ref}
        className="relative z-10 max-w-lg mx-auto text-center transition-all duration-700"
        style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(30px)' }}
      >
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[rgba(93,10,244,0.3)] bg-[rgba(93,10,244,0.08)] text-[#B89EF8] text-xs font-medium mb-5">
          {tr.badge}
        </div>
        <h2 className="text-3xl md:text-4xl font-bold text-white mb-3">{tr.title}</h2>
        <p className="text-[#6B6080] mb-12 text-base">{tr.subtitle}</p>

        <div
          className="rounded-3xl p-8 text-left relative overflow-hidden"
          style={{
            background: 'linear-gradient(135deg, rgba(93,10,244,0.15) 0%, rgba(30,10,80,0.4) 100%)',
            border: '1px solid rgba(93,10,244,0.4)',
            boxShadow: '0 32px 80px rgba(93,10,244,0.15), inset 0 1px 0 rgba(255,255,255,0.06)',
          }}
        >
          <div className="absolute top-0 right-0 w-40 h-40 bg-[rgba(93,10,244,0.2)] rounded-full blur-[60px] pointer-events-none" />
          <div className="relative z-10">
            <div className="flex items-start justify-between mb-6">
              <div>
                <div className="text-[#B89EF8] text-sm font-medium mb-1">{tr.planLabel}</div>
                <div className="flex items-end gap-1">
                  <span className="text-5xl font-bold text-white">27€</span>
                  <span className="text-[#6B6080] text-base mb-1.5">{tr.perMonth}</span>
                </div>
              </div>
              <div
                className="px-3 py-1.5 rounded-full text-xs font-bold tracking-wide"
                style={{ background: 'linear-gradient(135deg, rgba(93,10,244,0.4), rgba(124,58,237,0.3))', border: '1px solid rgba(93,10,244,0.5)', color: '#E0D4FF' }}
              >
                {tr.trialBadge}
              </div>
            </div>
            <div className="w-full h-px bg-[rgba(93,10,244,0.2)] mb-6" />
            <ul className="flex flex-col gap-3 mb-8">
              {tr.features.map((f) => (
                <li key={f} className="flex items-center gap-3 text-[#C4B5FD] text-sm">
                  <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(93,10,244,0.3)', border: '1px solid rgba(93,10,244,0.5)' }}>
                    <svg className="w-2.5 h-2.5 text-[#B89EF8]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  {f}
                </li>
              ))}
            </ul>
            <a
              href="/register"
              className="block w-full text-center py-4 rounded-2xl text-base font-semibold text-white transition-all duration-200 hover:opacity-90"
              style={{ background: 'linear-gradient(135deg, #5D0AF4, #7C3AED)', boxShadow: '0 8px 32px rgba(93,10,244,0.45)' }}
            >
              {tr.cta}
            </a>
            <p className="text-center text-[#4A4060] text-xs mt-3">{tr.note}</p>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Section 7: Testimonials ──────────────────────────────────────────────────
function TestimonialsSection() {
  const { lang } = useLang();
  const tr = t[lang].testimonials;
  const { ref, visible } = useScrollReveal();

  const avatarColors = ['#5D0AF4', '#10B981', '#F97316'];

  return (
    <section className="relative py-24 px-6 overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_#1A0A3E_0%,_#060510_70%)]" />
      <div
        ref={ref}
        className="relative z-10 max-w-5xl mx-auto transition-all duration-700"
        style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(30px)' }}
      >
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[rgba(93,10,244,0.3)] bg-[rgba(93,10,244,0.08)] text-[#B89EF8] text-xs font-medium mb-5">
            {tr.badge}
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-white">{tr.title}</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {tr.items.map((item, i) => (
            <div
              key={i}
              className="rounded-2xl p-6 flex flex-col gap-4 transition-all duration-300 hover:-translate-y-1"
              style={{
                background: 'linear-gradient(135deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.02) 100%)',
                border: '1px solid rgba(93,10,244,0.2)',
                boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
              }}
            >
              {/* Stars */}
              <div className="flex gap-1">
                {[...Array(5)].map((_, si) => (
                  <svg key={si} className="w-4 h-4" viewBox="0 0 20 20" fill="#F97316">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                ))}
              </div>
              {/* Quote */}
              <p className="text-[#A89EC4] text-sm leading-relaxed flex-1">&ldquo;{item.text}&rdquo;</p>
              {/* Author */}
              <div className="flex items-center gap-3 pt-2 border-t border-[rgba(255,255,255,0.05)]">
                <div
                  className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold text-white flex-shrink-0"
                  style={{ background: `linear-gradient(135deg, ${avatarColors[i]}, ${avatarColors[i]}99)` }}
                >
                  {item.avatar}
                </div>
                <div>
                  <div className="text-white text-sm font-semibold">{item.name}</div>
                  <div className="text-[#6B6080] text-xs">{item.role}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Section 8: FAQ ───────────────────────────────────────────────────────────
function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const { lang } = useLang();
  const tr = t[lang].faq;
  const { ref, visible } = useScrollReveal();

  return (
    <section id="faq" className="relative py-24 px-6 overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,_#130830_0%,_#060510_60%)]" />
      <div
        ref={ref}
        className="relative z-10 max-w-2xl mx-auto transition-all duration-700"
        style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(30px)' }}
      >
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[rgba(93,10,244,0.3)] bg-[rgba(93,10,244,0.08)] text-[#B89EF8] text-xs font-medium mb-5">
            {tr.badge}
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-white">{tr.title}</h2>
          <p className="text-[#6B6080] mt-3 text-base">{tr.subtitle}</p>
        </div>
        <div className="flex flex-col gap-3">
          {tr.items.map((item, i) => (
            <div
              key={i}
              className="rounded-2xl overflow-hidden transition-all duration-200"
              style={{
                background: openIndex === i ? 'rgba(93,10,244,0.1)' : 'rgba(255,255,255,0.03)',
                border: openIndex === i ? '1px solid rgba(93,10,244,0.35)' : '1px solid rgba(255,255,255,0.06)',
              }}
            >
              <button
                className="w-full flex items-center justify-between px-5 py-4 text-left"
                onClick={() => setOpenIndex(openIndex === i ? null : i)}
              >
                <span className="text-white text-sm font-medium pr-4">{item.question}</span>
                <div
                  className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center transition-all duration-200"
                  style={{
                    background: openIndex === i ? 'rgba(93,10,244,0.4)' : 'rgba(255,255,255,0.06)',
                    transform: openIndex === i ? 'rotate(180deg)' : 'rotate(0deg)',
                  }}
                >
                  <svg className="w-3 h-3 text-[#B89EF8]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </button>
              {openIndex === i && (
                <div className="px-5 pb-4">
                  <p className="text-[#8B7FAA] text-sm leading-relaxed">{item.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Section 9: CTA ───────────────────────────────────────────────────────────
function CTASection() {
  const { lang } = useLang();
  const { openModal } = useModal();
  const tr = t[lang].cta;
  const { ref, visible } = useScrollReveal();

  return (
    <section className="relative py-24 px-6 overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_#1A0A3E_0%,_#060510_70%)]" />
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div className="w-[600px] h-[300px] bg-[rgba(93,10,244,0.12)] rounded-full blur-[100px]" />
      </div>
      <div
        ref={ref}
        className="relative z-10 max-w-2xl mx-auto text-center transition-all duration-700"
        style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(30px)' }}
      >
        <h2 className="text-3xl md:text-5xl font-bold text-white leading-tight mb-5">
          {tr.title1}
          <br />
          <span style={{ background: 'linear-gradient(135deg, #8B5CF6 0%, #A78BFA 60%, #C4B5FD 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
            {tr.title2}
          </span>
        </h2>
        <p className="text-[#7A7090] text-base mb-10 max-w-md mx-auto">{tr.subtitle}</p>
        <a
          href="/register"
          className="inline-flex items-center gap-2 px-10 py-4 rounded-2xl text-base font-semibold text-white transition-all duration-200 hover:opacity-90 hover:scale-105"
          style={{ background: 'linear-gradient(135deg, #5D0AF4, #7C3AED)', boxShadow: '0 8px 40px rgba(93,10,244,0.5)' }}
        >
          {tr.cta}
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
          </svg>
        </a>
        <p className="text-[#4A4060] text-xs mt-4">{tr.note}</p>
      </div>
    </section>
  );
}

// ─── Footer ───────────────────────────────────────────────────────────────────
function Footer() {
  const { lang } = useLang();
  const tr = t[lang].footer;

  return (
    <footer id="footer" className="relative py-14 px-6 border-t border-[rgba(93,10,244,0.12)]" style={{ background: '#040309' }}>
      <div className="max-w-5xl mx-auto">
        <div className="flex flex-col md:flex-row items-center justify-between gap-8 mb-8">
          <div className="flex flex-col items-center md:items-start gap-2">
            <a href="#hero">
              <Image
                src="/assets/images/LOGO_REZERVO_HORIZONTALE-removebg-preview-1785868391038.png"
                alt="Rezervo Online Logo"
                width={240}
                height={60}
                className="h-[72px] w-auto object-contain opacity-80 hover:opacity-100 transition-opacity"
              />
            </a>
            <p className="text-[#3A3055] text-xs max-w-[200px] text-center md:text-left">{tr.tagline}</p>
          </div>
          <div className="flex items-center gap-6 flex-wrap justify-center">
            {tr.links.map((link) => (
              <a
                key={link.label}
                href={link.href}
                target={link.href.startsWith('http') ? '_blank' : undefined}
                rel={link.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                className="text-[#4A4060] hover:text-[#A89EC4] text-xs transition-colors duration-200"
              >
                {link.label}
              </a>
            ))}
          </div>
        </div>
        <div className="border-t border-[rgba(93,10,244,0.08)] pt-6 flex flex-col md:flex-row items-center justify-between gap-3">
          <div className="text-[#2E2845] text-xs">{tr.copy}</div>
          <div className="flex items-center gap-1.5">
            <div className="w-1.5 h-1.5 rounded-full bg-[#10B981]" />
            <span className="text-[#3A3055] text-xs">{lang === 'sq' ? 'Sistemi është aktiv' : 'System is live'}</span>
          </div>
        </div>
      </div>
    </footer>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function HomePage() {
  const [lang, setLang] = useState<Lang>('sq');
  const [modalOpen, setModalOpen] = useState(false);
  const [demoOpen, setDemoOpen] = useState(false);

  return (
    <LangContext.Provider value={{ lang, setLang }}>
      <ModalContext.Provider value={{ openModal: () => setModalOpen(true) }}>
        <main className="min-h-screen" style={{ background: '#060510' }}>
          <Navbar />
          <HeroSection onOpenDemo={() => setDemoOpen(true)} />
          <WhySection />
          <HowItWorksSection />
          <DashboardSection />
          <PersonalLinkSection />
          <PricingSection />
          <TestimonialsSection />
          <FAQSection />
          <CTASection />
          <Footer />
          <RegistrationModal isOpen={modalOpen} onClose={() => setModalOpen(false)} />
          <DemoModal isOpen={demoOpen} onClose={() => setDemoOpen(false)} />
        </main>
      </ModalContext.Provider>
    </LangContext.Provider>
  );
}
